import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import {
  Search, X, ChevronDown, ArrowLeft, Sparkles, MessageCircle, Paperclip,
  BookOpen, Users, Share2, CreditCard, Settings, LifeBuoy, Smartphone,
  Monitor, Mail,
} from 'lucide-react';
import { FAQ_CATEGORIES, FAQ_ITEMS as FAQ_ITEMS_SEED } from '@/data/faqContent';

import { useTranslation } from 'react-i18next';

import { canGoBackInApp } from '@/utils/useSafeBack';
const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  Sparkles, MessageCircle, Paperclip, BookOpen, Users, Share2,
  CreditCard, Settings, LifeBuoy,
};

// Support email — swap in .env or Settings later.
const SUPPORT_EMAIL = 'support@askeili.com';

/* Screenshot with an optional viewport-toggle. Answers can include one
 * mobile + one desktop shot; on wide screens both render side-by-side,
 * on mobile the desktop image collapses under a "See desktop view" toggle
 * to keep the flow tight. */
function ScreenshotGallery({ shots }) {
  const {
    t
  } = useTranslation();

  const [showDesktop, setShowDesktop] = useState(false);
  if (!shots || shots.length === 0) return null;
  const mobile = shots.find((s) => s.viewport === 'mobile');
  const desktop = shots.find((s) => s.viewport === 'desktop');

  return (
    <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="faq-screenshots">
      {mobile && (
        <figure className="rounded-xl overflow-hidden border border-border/60 bg-muted/30">
          <img
            src={mobile.src}
            alt={mobile.alt}
            loading="lazy"
            className="w-full max-h-[520px] object-contain bg-black/5"
            data-testid="faq-shot-mobile"
          />
          <figcaption className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Smartphone className="h-3 w-3" /> {t('generated.faqpage_mobile_1d9c')}
          </figcaption>
        </figure>
      )}
      {desktop && (
        <>
          {/* Desktop image always visible on md+, toggle on mobile */}
          <div className="hidden md:block">
            <figure className="rounded-xl overflow-hidden border border-border/60 bg-muted/30">
              <img
                src={desktop.src}
                alt={desktop.alt}
                loading="lazy"
                className="w-full max-h-[520px] object-contain bg-black/5"
                data-testid="faq-shot-desktop"
              />
              <figcaption className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Monitor className="h-3 w-3" /> {t('generated.faqpage_desktop_750e')}
              </figcaption>
            </figure>
          </div>
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setShowDesktop((v) => !v)}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid="faq-toggle-desktop"
            >
              <Monitor className="h-3 w-3" />
              {showDesktop ? 'Hide desktop view' : 'See desktop view'}
            </button>
            {showDesktop && (
              <figure className="mt-2 rounded-xl overflow-hidden border border-border/60 bg-muted/30">
                <img
                  src={desktop.src}
                  alt={desktop.alt}
                  loading="lazy"
                  className="w-full object-contain bg-black/5"
                />
              </figure>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* Individual Q&A accordion. Deep-linkable via hash. */
function FAQItem({ item, isOpen, onToggle }) {
  const ref = useRef(null);
  useEffect(() => {
    if (isOpen && ref.current) {
      // Auto-scroll opened item into view (nice for hash-nav landings).
      setTimeout(() => {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  }, [isOpen]);

  return (
    <article
      ref={ref}
      id={`faq-${item.id}`}
      className="border-b border-border/60 last:border-b-0"
      data-testid={`faq-item-${item.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-start justify-between gap-3 py-4 px-1 text-left group"
        data-testid={`faq-question-${item.id}`}
      >
        <span className="text-sm md:text-base font-medium leading-snug group-hover:text-primary transition-colors">
          {item.question}
        </span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180 text-primary' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div
          className="pb-5 pl-1 pr-1 text-sm text-muted-foreground leading-relaxed space-y-3 faq-answer-prose break-words"
          data-testid={`faq-answer-${item.id}`}
        >
          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => (
                <a {...props} className="text-primary underline hover:no-underline break-all" />
              ),
              code: ({ node, ...props }) => (
                <code {...props} className="px-1 py-0.5 rounded bg-muted text-xs font-mono break-all" />
              ),
              ul: ({ node, ...props }) => (
                <ul {...props} className="list-disc pl-5 space-y-1.5" />
              ),
              ol: ({ node, ...props }) => (
                <ol {...props} className="list-decimal pl-5 space-y-1.5" />
              ),
              p: ({ node, ...props }) => <p {...props} className="leading-relaxed" />,
            }}
          >
            {typeof item.answer === 'string' ? item.answer : ''}
          </ReactMarkdown>
          <ScreenshotGallery shots={item.screenshots} />
        </div>
      )}
    </article>
  );
}

export default function FAQPage() {
  const {
    t,
    i18n
  } = useTranslation();

  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [items, setItems] = useState(FAQ_ITEMS_SEED);

  // Per-page title + meta (audit D5.1) — restores on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    document.title = 'FAQ · Eili — Bible Study Companion';
    if (meta) meta.setAttribute('content', 'Answers to common questions about Eili: how Scripture accuracy works, the difference between Free and Premium, denominational stance, and how your data is used.');
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute('content', prevDesc);
    };
  }, []);

  // Fetch live content from the admin-editable API. Fall back silently
  // to the shipped seed if the endpoint isn't available (older builds)
  // or returns empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lang = (i18n.language || 'en').split('-')[0];
        const res = await axios.get(`${API}/api/faq`, {
          params: { locale: lang },
          withCredentials: true,
        });
        if (cancelled) return;
        if (Array.isArray(res.data?.items) && res.data.items.length > 0) {
          setItems(res.data.items);
        }
      } catch (_) {
        // Silent — seed content is already rendered.
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  // FAQ JSON-LD schema for search / AI-discovery (audit D5.5).
  // Only emits if the FAQ item's answer is plain-text-safe (no HTML) and
  // there's at least one question — otherwise Google will complain about
  // malformed markup.
  useEffect(() => {
    if (!items || items.length === 0) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'eili-faq-jsonld';
    const data = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.slice(0, 40).map((it) => ({
        '@type': 'Question',
        name: it.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: (it.answer || '').replace(/<[^>]+>/g, '').slice(0, 900),
        },
      })),
    };
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('eili-faq-jsonld');
      if (existing) existing.remove();
    };
  }, [items]);

  // Deep-link handling — /faq#question-id opens that item on load.
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    // Support both "faq-<id>" and raw "<id>".
    const id = hash.startsWith('faq-') ? hash.slice(4) : hash;
    if (items.some((i) => i.id === id)) {
      setOpenIds(new Set([id]));
      const match = items.find((i) => i.id === id);
      if (match) setActiveCategory(match.category);
    }
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (activeCategory !== 'all' && item.category !== activeCategory) return false;
      if (!q) return true;
      const hay = `${item.question} ${(item.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, activeCategory, items]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const cat of FAQ_CATEGORIES) map.set(cat.id, []);
    for (const item of filtered) {
      if (map.has(item.category)) map.get(item.category).push(item);
    }
    return map;
  }, [filtered]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Keep hash in sync so URL is copy-shareable.
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#faq-${id}`);
    }
  };

  // Back-button behaviour: return the user to whatever page they were on
  // before entering /faq. If they landed directly (deep-link, PWA cold
  // start, opened in a new tab), there's no history to pop — send them
  // to the home screen instead. In-page toggles use `history.replaceState`
  // (see the `toggle` above), so popping history always exits /faq — it
  // never loops within the FAQ page.
  const handleBack = () => {
    if (canGoBackInApp()) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  const expandAll = () => setOpenIds(new Set(filtered.map((i) => i.id)));
  const collapseAll = () => setOpenIds(new Set());

  return (
    <div className="page-container bg-background overflow-x-hidden" data-testid="faq-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/85 backdrop-blur border-b border-border/60">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="p-2 -ml-2 rounded-lg hover:bg-muted transition-colors"
            aria-label={t('generated.faqpage_back_1073')}
            data-testid="faq-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold leading-tight truncate">{t('generated.faqpage_help_faq_b6c5')}</h1>
            <p className="text-xs text-muted-foreground truncate">
              {t('generated.faqpage_answers_walk_throughs_5d15')}
            </p>
          </div>
        </div>
      </header>
      <main className="page-content w-full max-w-4xl mx-auto px-4 pb-24 overflow-x-hidden">
        {/* Search */}
        <div className="mt-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('generated.faqpage_search_questions_upload_8d68')}
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            data-testid="faq-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-muted"
              aria-label={t('generated.faqpage_clear_search_32d7')}
              data-testid="faq-search-clear"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Category chips */}
        <nav
          className="mt-4 -mx-4 px-4 flex gap-2 overflow-x-auto scrollbar-hide pb-1"
          data-testid="faq-categories"
        >
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              activeCategory === 'all'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
            }`}
            data-testid="faq-cat-all"
          >
            {t('generated.faqpage_all_a86d')}
          </button>
          {FAQ_CATEGORIES.map((cat) => {
            const Icon = ICONS[cat.icon] || Sparkles;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                }`}
                data-testid={`faq-cat-${cat.id}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {cat.label}
              </button>
            );
          })}
        </nav>

        {/* Actions strip */}
        <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="faq-result-count">
            {filtered.length} question{filtered.length === 1 ? '' : 's'}
            {query && ` matching "${query}"`}
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={expandAll}
              className="hover:text-primary transition-colors"
              data-testid="faq-expand-all"
            >
              {t('generated.faqpage_expand_all_54cf')}
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="hover:text-primary transition-colors"
              data-testid="faq-collapse-all"
            >
              {t('generated.faqpage_collapse_all_2c44')}
            </button>
          </div>
        </div>

        {/* Grouped Q&A */}
        <div className="mt-4 space-y-8">
          {FAQ_CATEGORIES.map((cat) => {
            const items = grouped.get(cat.id) || [];
            if (items.length === 0) return null;
            const Icon = ICONS[cat.icon] || Sparkles;
            return (
              <section
                key={cat.id}
                id={cat.id}
                className="rounded-2xl border border-border/60 bg-card overflow-hidden"
                data-testid={`faq-section-${cat.id}`}
              >
                <header className="px-5 py-3 border-b border-border/60 bg-muted/30 flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold">{cat.label}</h2>
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {items.length}
                  </span>
                </header>
                <div className="px-5">
                  {items.map((item) => (
                    <FAQItem
                      key={item.id}
                      item={item}
                      isOpen={openIds.has(item.id)}
                      onToggle={() => toggle(item.id)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div
            className="mt-10 text-center py-14 rounded-2xl border border-dashed border-border/60 bg-card/30"
            data-testid="faq-empty"
          >
            <Search className="h-8 w-8 text-muted-foreground mx-auto mb-3 opacity-60" />
            <p className="text-sm font-medium">{t('generated.faqpage_nothing_matched_106c')}{query}"</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('generated.faqpage_try_a_shorter_f1da')}
            </p>
            <button
              type="button"
              onClick={() => { setQuery(''); setActiveCategory('all'); }}
              className="mt-4 text-xs text-primary hover:underline"
              data-testid="faq-reset"
            >
              {t('generated.faqpage_reset_filters_acc6')}
            </button>
          </div>
        )}

        {/* Still-need-help contact card */}
        <section
          className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4"
          data-testid="faq-contact"
        >
          <div className="flex-1">
            <h3 className="text-sm font-semibold">{t('generated.faqpage_still_stuck_ff04')}</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {t('generated.faqpage_drop_us_a_bfaa')}
            </p>
          </div>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Eili%20help%20request`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            data-testid="faq-email-btn"
          >
            <Mail className="h-4 w-4" />
            {t('generated.faqpage_email_support_1fab')}
          </a>
        </section>
      </main>
    </div>
  );
}
